import { useEffect, useMemo, useState } from "react";
import {
    Box, Button, FormControl, FormControlLabel, InputLabel, MenuItem,
    Radio, RadioGroup, Select, TextField, Typography
} from "@mui/material";
import { useSnackbar } from "notistack";
import { apiConstants, getGeneric, postGeneric } from "../../common/constants";

// reason_master categories – must match the backend constants in PreOrderServiceImpl.
const CATEGORY_BY_PARTY = {
    CLIENT: "CLIENT_FAILURE_REASONS",
    COURIER: "COURIER_FAILURE_REASONS",
};

/**
 * Modal body for marking a PUR as PICKUP_FAILED. Lets ops pick the failure party
 * (Client / Courier), then a mandatory reason loaded from the matching reason_master
 * category, and an optional remark (required when the reason is "Other").
 *
 * Rendered as the child of a MUIModal. Props:
 *   row        – the pickup request row
 *   handleClose – closes the modal
 *   onDone      – called after a successful close (refetch + summary refresh)
 */
export default function MarkPickupFailedModal({ row, handleClose, onDone }) {
    const { enqueueSnackbar } = useSnackbar();
    const [failureParty, setFailureParty] = useState("");
    const [reasonCode, setReasonCode] = useState("");
    const [remarks, setRemarks] = useState("");
    const [reasonOptions, setReasonOptions] = useState([]);
    const [loadingReasons, setLoadingReasons] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Load reasons whenever the failure party changes.
    useEffect(() => {
        if (!failureParty) {
            setReasonOptions([]);
            return;
        }
        setReasonCode("");
        setLoadingReasons(true);
        getGeneric(apiConstants.REASON_MASTER, { category: CATEGORY_BY_PARTY[failureParty], activeOnly: true })
            .then((res) => {
                const list = Array.isArray(res?.data) ? res.data : (res?.data?.data || res?.data?.responseObj || []);
                setReasonOptions((Array.isArray(list) ? list : []).map((r) => ({ value: r.code, label: r.label || r.code })));
            })
            .catch(() => setReasonOptions([]))
            .finally(() => setLoadingReasons(false));
    }, [failureParty]);

    const isOther = useMemo(() => reasonCode && reasonCode.toUpperCase() === "OTHER", [reasonCode]);

    const handleSubmit = () => {
        if (!failureParty) {
            enqueueSnackbar("Select who the pickup failed by (Client or Courier).", { variant: "warning" });
            return;
        }
        if (!reasonCode) {
            enqueueSnackbar("Select a failure reason.", { variant: "warning" });
            return;
        }
        if (isOther && !remarks.trim()) {
            enqueueSnackbar("Remarks are required for the 'Other' reason.", { variant: "warning" });
            return;
        }
        setSubmitting(true);
        postGeneric(apiConstants.CLOSE_PICKUP, {
            references: [row.reference],
            merchantOid: row.merchantDetail?.key,
            status: "PICKUP_FAILED",
            failureParty,
            reasonCode,
            remarks: remarks.trim() || undefined,
        })
            .then((response) => {
                const results = response?.data || [];
                const failedItem = Array.isArray(results) ? results.find((r) => r && r.success === false) : null;
                if (failedItem) {
                    enqueueSnackbar(failedItem.message || "Could not mark pickup as failed", { variant: "warning" });
                } else {
                    enqueueSnackbar("Pickup marked as failed", { variant: "success" });
                }
                if (onDone) onDone();
                handleClose();
            })
            .catch((err) => {
                const message = err?.response?.data?.message || "Something went wrong while marking pickup as failed";
                enqueueSnackbar(message, { variant: "error" });
            })
            .finally(() => setSubmitting(false));
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 1, minWidth: 360 }}>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                Reference: <b>{row?.reference}</b>
            </Typography>

            <FormControl>
                <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>Failed by</Typography>
                <RadioGroup row value={failureParty} onChange={(e) => setFailureParty(e.target.value)}>
                    <FormControlLabel value="CLIENT" control={<Radio size="small" />} label="Client" />
                    <FormControlLabel value="COURIER" control={<Radio size="small" />} label="Courier" />
                </RadioGroup>
            </FormControl>

            <FormControl fullWidth size="small" disabled={!failureParty || loadingReasons}>
                <InputLabel>Failure reason</InputLabel>
                <Select value={reasonCode} label="Failure reason" onChange={(e) => setReasonCode(e.target.value)}>
                    <MenuItem value="">—</MenuItem>
                    {reasonOptions.map((r) => (
                        <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                    ))}
                </Select>
                {failureParty && !loadingReasons && reasonOptions.length === 0 && (
                    <Typography sx={{ fontSize: 11, color: "#dc2626", mt: 0.5 }}>
                        No reasons configured — seed reason_master (category {CATEGORY_BY_PARTY[failureParty]}).
                    </Typography>
                )}
            </FormControl>

            <TextField
                label={isOther ? "Remarks (required)" : "Remarks (optional)"}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                size="small"
                multiline
                minRows={2}
                fullWidth
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button onClick={handleClose} size="small">Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="error"
                    size="small"
                    disabled={submitting}
                >
                    Mark Failed
                </Button>
            </Box>
        </Box>
    );
}
